import {
  PackageSchemaTypeEnum,
  PackageStatusDetailsLevelEnum,
  PackageTypeEnum,
  SortOrderEnum,
} from '../domain';
import { z } from 'zod';
import { packageLevel, packageStatus } from './propTransformer';

export enum PackagesOrderByEnum {
  createdAt = 'createdAt',
  created = 'created', // override
  updatedAt = 'updatedAt',
  label = 'label',
  version = 'version',
  versionInt = 'versionInt',
}

const ArrayOfStringsOrString = z
  .string()
  .or(z.string().array())
  .transform((value) => (Array.isArray(value) ? value : [value]));

export const GetPackagesQueryFilter = z.object({
  filter: z
    .object({
      id: z.string().optional(),
      schemaType: z.nativeEnum(PackageSchemaTypeEnum).optional(),
      level: z
        .array(z.nativeEnum(PackageStatusDetailsLevelEnum))
        .or(z.nativeEnum(PackageStatusDetailsLevelEnum))
        .transform((value) => (Array.isArray(value) ? value : [value]))
        .optional(),
      category: z.optional(ArrayOfStringsOrString),
    })
    .optional(),
});

export const GetPackagesQuery = z.intersection(
  GetPackagesQueryFilter,
  z
    .object({
      id: z.optional(ArrayOfStringsOrString),
      type: z.nativeEnum(PackageTypeEnum).optional(),
      query: z
        .string()
        .optional()
        .transform((s) => (typeof s === 'string' ? s.trim() : s)),
      scope: z
        .object({
          id: z.optional(ArrayOfStringsOrString),
          category: z.optional(ArrayOfStringsOrString),
        })
        .optional(),
      size: z
        .number()
        .or(z.string())
        .transform((n) => Number(n) || 10)
        .optional(),
      cursor: z
        .object({
          from: z
            .number()
            .or(z.string())
            .transform((n) => Number(n) || 10)
            .optional(),
        })
        .optional(),
      orderBy: z.nativeEnum(PackagesOrderByEnum).optional(),
      order: z.nativeEnum(SortOrderEnum).optional(),
      categories: z.optional(ArrayOfStringsOrString),
      includeDeprecated: z
        .string()
        .transform((v) => ['true', '1', 1, true].includes(v))
        .or(z.boolean())
        .optional(),
      includePrerelease: z
        .string()
        .transform((v) => ['true', '1', 1, true].includes(v))
        .or(z.boolean())
        .optional(),
    })
    .optional(),
);

export const PackageParamUniversal = z.object({
  type: z.nativeEnum(PackageTypeEnum),
  id: z.string(),
  version: z.string().optional(),
  revisionId: z.string().optional(),
  updateSearch: z.boolean().optional()
});

export const CreateReleaseParams = z.object({
  type: z.nativeEnum(PackageTypeEnum),
  id: z.string(),
  revisionId: z.string().optional(),
  version: z.string().optional(),
  level: packageLevel,
});

export const PackageParamNpmScope = z.object({
  npmScope: z.string().optional(),
});

export const PackageIncludesParams = z.object({
  includeDeprecated: z
    .string()
    .transform((v) => ['true', '1', 1, true].includes(v))
    .or(z.boolean())
    .optional(),
  includeMember: z
    .string()
    .transform((v) => ['true', '1', 1, true].includes(v))
    .or(z.boolean())
    .optional(),
  includeReleaseNotes: z
    .string()
    .transform((v) => ['true', '1', 1, true].includes(v))
    .or(z.boolean())
    .optional(),
  includeData: z
    .string()
    .transform((v) => ['true', '1', 1, true].includes(v))
    .or(z.boolean())
    .optional(),
  includePrerelease: z
    .string()
    .transform((v) => ['true', '1', 1, true].includes(v))
    .or(z.boolean())
    .optional(),
});

export const PackageSetSystemStatusParams = z.object({
  type: z.nativeEnum(PackageTypeEnum),
  id: z.string(),
  version: z.string().optional(),
  revisionId: z.string().optional(),
  status: packageStatus,
});
